import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Role} from '../../model/auth/Role';
import {ProgressService} from '../../service/progress.service';
import {BaseModalComponent} from '../../common/modal/base-modal/base-modal.component';
import {NotificationHandler} from '../../helper/notification.handler';
import {Contest} from '../../model/contest/Contest';
import {ContestService} from '../../service/contest.service';
import {DomSanitizer} from '@angular/platform-browser';
import {EventCategory, EventType, TrackingService} from '../../service/tracking.service';
import {EnrolData} from '../../model/auth/User';
import {MultiActionItem} from '../../common/button/button.component';
import {ErrorInterceptor} from '../../helper/error.interceptor';
import {SchoolBaseDto} from '../../model/location/School';

export interface ContestEnrolData {
  title: string;
  contest?: Contest;

  userId?: string;
  role?: Role;
  group?: EnrolData[];
  school?: SchoolBaseDto;
  regionId?: string;

  freeEnrolAllowed?: boolean;
}

export interface ContestEnrolResultData {
  paymentPageUrl?: string;
  exemptEnrol: boolean;
  freeEnrol?: boolean;

  freeEnrolRequested?: boolean;
}

@Component({
  selector: 'app-contest-enrol-modal',
  templateUrl: './contest-enrol-modal.component.html',
  styleUrls: ['./contest-enrol-modal.component.scss']
})
export class ContestEnrolModalComponent extends BaseModalComponent {

  readonly roles = [Role.Host, Role.Coordinator, Role.Teacher, Role.Student];

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;

  title!: string;
  readonly contestId?: string;
  readonly group?: EnrolData[];
  readonly school?: SchoolBaseDto;
  readonly regionId?: string;
  contest?: Contest;

  readonly forUserId?: string;
  readonly forRole?: Role;

  freeEnrol = false;
  freeEnrolAllowed?: boolean;
  exemptEnrolAllowed?: boolean;

  ctaActions?: MultiActionItem[];

  constructor(private contestService: ContestService,
              public sanitizer: DomSanitizer,
              private trackingService: TrackingService,
              dialogRef: MatDialogRef<ContestEnrolModalComponent>,
              progressService: ProgressService,
              @Inject(MAT_DIALOG_DATA) data: ContestEnrolData) {
    super(dialogRef, progressService);

    this.title = data.title;

    if (data) {
      if (data.contest && data.contest.id) {
        this.contestId = data.contest.id;
      }
      this.group = data.group;
      this.forRole = data.role;
      this.forUserId = data.userId;
      this.school = data.school;
      this.regionId = data.regionId;

      this.freeEnrolAllowed = data.freeEnrolAllowed;
    }
  }

  initContent() {
    if (this.contestId) {
      this.loadContest(this.contestId);
    }
  }

  private loadContest(id: string) {
    this.startProgress();

    this.contestService.getDetails(id).subscribe({
      next: contest => {
        this.contest = contest;

        if (this.contest?.price && this.contest?.price > 0 && !this.contest?.paymentRequired) {
          this.freeEnrolAllowed = true;
        }

        this.trackingService.sendEvent(EventType.view_item, EventCategory.purchase, contest.name);
        this.updateCtaActions();

        this.stopProgress();
      }, error: error => {
        NotificationHandler.handleError($localize`:Notifications|Contest details fetch has failed message@@notifications-contest-details-fetch-failed-error:Contest details fetch has failed`, error);
        this.stopProgress();
      }
    });
  }

  rolesUpdated(roles: Role[]) {
    if ((this.forRole && this.forRole == Role.Teacher) || (!this.forRole && roles.includes(Role.Teacher))) {
      this.freeEnrol = true;
    }

    if (roles.includes(Role.Coordinator) || roles.includes(Role.Teacher)) {
      this.exemptEnrolAllowed = true;
    }
  }

  proceedAllowed(): boolean {
    return true;
  }

  updateCtaActions() {
    const actions: MultiActionItem[] = [];

    if (this.contest && !this.freeEnrol && this.exemptEnrolAllowed && this.contest.price && this.contest.price > 0) {
      actions.push({
        label: $localize`:General|Exempt Enrol action label@@general-exempt-enrol-action-label:Exempt Enrol`,
        action: 'exempt-enrol',
        icon: 'redeem'
      });
    }
    if (this.contest && this.freeEnrolAllowed) {
      actions.push({
        label: $localize`:General|Free Enrol action label@@general-free-enrol-action-label:Free Enrol`,
        action: 'free-enrol',
        icon: 'how_to_reg'
      });
    }

    this.ctaActions = actions;
  }

  ctaActionSelected(action: string) {
    switch (action) {
      case 'exempt-enrol':
        this.exemptionEnrol();
        break;
      case 'free-enrol':
        this.enrolUserForFree();
        break;
    }
  }

  proceed(): void {
    if (!this.contest) return;

    this.startProgress();

    this.contestService.enrol(this.contest.id, this.forUserId, this.forRole, undefined, this.school).subscribe({
      next: user => {
        this.stopProgress();

        const data: ContestEnrolResultData = {
          paymentPageUrl: user.paymentPageUrl || '',
          freeEnrol: !user.paymentPageUrl,
          exemptEnrol: false
        };

        if (user.paymentNotConfigured) {
          NotificationHandler.handleWarning($localize`:Notifications|Tenant payment provider not configured message@@notifications-tenant-payment-provider-not-configured-error:The Host does not configure a payment provider for this competition. Please get in touch with the Host directly to proceed with payment.`, false);
        }

        this.closeModal(data);
      }, error: error => {
        this.stopProgress();

        const code = ErrorInterceptor.getStatusCode(error);

        if (code == 412) {
          NotificationHandler.handleWarning($localize`:Notifications|Tenant payment provider not configured message@@notifications-tenant-payment-provider-not-configured-error:The Host does not configure a payment provider for this competition. Please get in touch with the Host directly to proceed with payment.`, error, false);
        } else {
          NotificationHandler.handleError($localize`:Notifications|Contest enrol has failed message@@notifications-contest-enrol-failed-error:Contest enrol has failed`, error);
        }
      }
    });
  }

  exemptionEnrol() {
    const data: ContestEnrolResultData = {
      exemptEnrol: true
    };

    this.closeModal(data);
  }

  enrolUserForFree() {
    const data: ContestEnrolResultData = {
      exemptEnrol: false,
      freeEnrolRequested: true
    };

    this.closeModal(data);
  }

  payForGroup() {
    if (!this.contest || !this.group || !this.regionId) return;

    this.startProgress();

    this.contestService.enrolGroup(this.contest.id, this.group, this.regionId, this.school).subscribe({
      next: group => {
        this.stopProgress();

        if (group.paymentNotConfigured) {
          NotificationHandler.handleWarning($localize`:Notifications|Tenant payment provider not configured message@@notifications-tenant-payment-provider-not-configured-error:The Host does not configure a payment provider for this competition. Please get in touch with the Host directly to proceed with payment.`, false);
        }

        const data: ContestEnrolResultData = {
          paymentPageUrl: group.paymentPageUrl || '',
          freeEnrol: false,
          exemptEnrol: false
        };

        this.closeModal(data);
      }, error: error => {
        this.stopProgress();

        const code = ErrorInterceptor.getStatusCode(error);

        if (code == 412) {
          NotificationHandler.handleWarning($localize`:Notifications|Tenant payment provider not configured message@@notifications-tenant-payment-provider-not-configured-error:The Host does not configure a payment provider for this competition. Please get in touch with the Host directly to proceed with payment.`, error, false);
        } else {
          NotificationHandler.handleError($localize`:Notifications|Contest enrol has failed message@@notifications-contest-enrol-failed-error:Contest enrol has failed`, error);
        }
      }
    });
  }
}
